import { useLayoutEffect, useMemo, useState, type RefObject } from 'react';
import { DocumentDownload, CloseSquare, SearchNormal1 } from 'iconsax-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '@design-system/dialog';
import { Input } from '@design-system/input';
import type { TablePresentation } from '../../types/presentation.types';
import { type OpenRecordFn } from '../../hooks/use-open-record';
import { AgentDataTable } from './agent-data-table';

type AnchorRect = {
  top: number;
  right: number;
  height: number;
};

/** ~90% of the assistant aside height, vertically centered on it. */
function measureBesideAside(aside: HTMLElement): AnchorRect {
  const r = aside.getBoundingClientRect();
  const height = r.height * 0.9;
  return {
    top: r.top + (r.height - height) / 2,
    right: Math.max(0, window.innerWidth - r.left - 15),
    height,
  };
}

function filterRows(
  block: TablePresentation,
  query: string
): TablePresentation {
  const q = query.trim().toLowerCase();
  if (!q) return block;
  return {
    ...block,
    rows: block.rows.filter((row) =>
      block.columns.some((col) => {
        const value = row[col.key];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(q);
      })
    ),
  };
}

function downloadCsv(block: TablePresentation) {
  const escape = (v: unknown) => {
    const s = v === null || v === undefined ? '' : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const header = block.columns.map((c) => escape(c.label)).join(',');
  const lines = block.rows.map((row) =>
    block.columns.map((c) => escape(row[c.key])).join(',')
  );
  const blob = new Blob([[header, ...lines].join('\n')], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(block.title || 'table').replace(/\s+/g, '-').toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Table presentation dialog (Figma node 20420:441699).
 * Overlay portal, anchored left of the AI aside.
 */
export function AgentTableModal({
  open,
  onClose,
  block,
  onOpenRecord,
  asideRef,
}: {
  open: boolean;
  onClose: () => void;
  block: TablePresentation;
  onOpenRecord?: OpenRecordFn;
  asideRef: RefObject<HTMLElement | null>;
}) {
  const [pos, setPos] = useState<AnchorRect | null>(null);
  const [query, setQuery] = useState('');

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      setQuery('');
      return;
    }

    const update = () => {
      const aside = asideRef.current;
      if (!aside) return;
      setPos(measureBesideAside(aside));
    };

    update();
    window.addEventListener('resize', update);
    const ro = new ResizeObserver(update);
    if (asideRef.current) ro.observe(asideRef.current);

    return () => {
      window.removeEventListener('resize', update);
      ro.disconnect();
    };
  }, [open, asideRef]);

  const filtered = useMemo(() => filterRows(block, query), [block, query]);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent
        dialogOverlayProps={{
          className: 'bg-transparent',
        }}
        data-slot='agent-table-modal'
        align='left'
        animation='zoom'
        aria-describedby={undefined}
        className='flex max-w-fit w-fit flex-col gap-2 overflow-hidden rounded-l-2xl rounded-r-none border border-primary-200 bg-white p-0 shadow-[0px_0px_8px_0px_rgba(0,0,0,0.15)] sm:rounded-l-2xl'
        style={
          pos
            ? {
                top: pos.top,
                right: pos.right,
                height: pos.height,
                left: 'auto',
                bottom: 'auto',
                margin: 0,
                transform: 'none',
              }
            : { visibility: 'hidden' }
        }
      >
        {/* Title bar — download | title | close (Figma 20420:441701) */}
        <div className='flex h-14 shrink-0 items-center gap-2 px-4'>
          <button
            type='button'
            aria-label='Download table'
            onClick={() => downloadCsv(filtered)}
            className='inline-flex size-6 shrink-0 items-center justify-center text-gray-800 transition-opacity hover:opacity-80'
          >
            <DocumentDownload
              size={24}
              color='currentColor'
              variant='Outline'
            />
          </button>
          <DialogTitle className='min-w-0 flex-1 truncate text-center text-base font-semibold capitalize leading-[1.2] text-gray-900'>
            {block.title || 'Table'}
          </DialogTitle>
          <DialogClose
            aria-label='Close table'
            className='inline-flex size-6 shrink-0 items-center justify-center text-gray-900 opacity-70 transition-opacity hover:opacity-100'
          >
            <CloseSquare
              size={24}
              color='currentColor'
              variant='Outline'
            />
            <span className='sr-only'>Close</span>
          </DialogClose>
        </div>

        <div className='flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border border-primary-200'>
          {/* Search — Figma 20420:441791 */}
          <div className='flex h-[72px] shrink-0 items-center border-b border-primary-200 bg-white px-4'>
            <Input
              type='search'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Search ..'
              size='sm'
              variant='fill'
              prefixIcon={
                <SearchNormal1
                  size={16}
                  color='#827599'
                  variant='Outline'
                  className='size-4 shrink-0'
                />
              }
              className='w-full rounded-xl border-primary-200 bg-primary-30'
              inputClassName='placeholder:text-gray-600'
              aria-label='Search table'
            />
          </div>

          <div className='min-h-0 flex-1 overflow-auto'>
            <AgentDataTable
              block={filtered}
              onOpenRecord={onOpenRecord}
              hideTitle
              columnSortHint
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
